import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Shared bill-header grid used by RM Inward + Job Work Invoice (Feb 2026).
// Fields (in reading order):
//   Row 1 col 1: Bill # * · [Order Number when showOrderNumber]
//   Row 1 col 2: Bill Date * · Due Date
//   Row 2 col 1: Payment Terms
//   Row 2 col 2: Accounts Payable (default "Trade Payables")
//
// `showOrderNumber={false}` hides the Order Number field entirely
// (Job Work Invoice per AC7 — job-work bills don't carry PO refs).
export const PAYMENT_TERMS = [
  { value: "NET_7", label: "Net 7", days: 7 },
  { value: "NET_15", label: "Net 15", days: 15 },
  { value: "NET_30", label: "Net 30", days: 30 },
  { value: "NET_45", label: "Net 45", days: 45 },
  { value: "NET_60", label: "Net 60", days: 60 },
  { value: "DUE_ON_RECEIPT", label: "Due on Receipt", days: 0 },
  { value: "CUSTOM", label: "Custom", days: null },
];

const ACCOUNTS_PAYABLE_OPTIONS = [
  "Trade Payables",
  "Sundry Creditors",
  "Other Payables",
];

export const BillHeaderFields = ({
  data,
  onChange,
  showOrderNumber = true,
  billNumberTestId = "bill-number-input",
  billDateTestId = "bill-date-input",
  billNumberPlaceholder = "e.g., INV-2026-001",
}) => {
  const set = (patch) => onChange({ ...data, ...patch });

  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase">Bill # *</Label>
            <Input
              value={data.bill_number || ""}
              onChange={(e) => set({ bill_number: e.target.value })}
              placeholder={billNumberPlaceholder}
              data-testid={billNumberTestId}
              className="font-mono"
            />
          </div>
          {showOrderNumber && (
            <div>
              <Label className="text-xs font-bold uppercase">Order Number</Label>
              <Input
                value={data.order_number || ""}
                onChange={(e) => set({ order_number: e.target.value })}
                placeholder="PO reference"
                className="font-mono"
              />
            </div>
          )}
        </div>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase">Bill Date *</Label>
            <Input
              type="date"
              value={data.bill_date || ""}
              onChange={(e) => set({ bill_date: e.target.value })}
              data-testid={billDateTestId}
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase">Due Date</Label>
            <Input
              type="date"
              value={data.due_date || ""}
              onChange={(e) => set({ due_date: e.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-4">
        <div>
          <Label className="text-xs font-bold uppercase">Payment Terms</Label>
          <Select
            value={data.payment_terms || undefined}
            onValueChange={(v) => set({ payment_terms: v })}
          >
            <SelectTrigger data-testid="payment-terms-select">
              <SelectValue placeholder="Select payment terms" />
            </SelectTrigger>
            <SelectContent>
              {PAYMENT_TERMS.map((pt) => (
                <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase">Accounts Payable</Label>
          <Select
            value={data.accounts_payable || undefined}
            onValueChange={(v) => set({ accounts_payable: v })}
          >
            <SelectTrigger data-testid="accounts-payable-select">
              <SelectValue placeholder="Select account" />
            </SelectTrigger>
            <SelectContent>
              {ACCOUNTS_PAYABLE_OPTIONS.map((a) => (
                <SelectItem key={a} value={a}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </>
  );
};

export default BillHeaderFields;
